import {
  Box,
  Paper,
  TableContainer,
  TablePagination,
  Table as MuiTable,
  BoxProps,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  getSortedRowModel,
  InitialTableState,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { TableSkeleton } from "./TableSkeleton";
import { TableHead } from "./TableHead";
import { TableBody } from "./TableBody";
import { useEffect } from "react";

export const Table = <T extends object>({
  data,
  columns,
  initialState,
  loading,
  onVisibleRowsChange,
  sx,
  ...boxProps
}: {
  data: T[];
  columns: ColumnDef<T>[];
  initialState?: InitialTableState;
  loading?: boolean;
  onVisibleRowsChange?: (rows: T[]) => void;
} & BoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      ...initialState,
      pagination: initialState?.pagination ?? {
        pageSize: 25,
      },
      sorting: initialState?.sorting ?? [],
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
    },
  });

  const { pagination, sorting } = table.getState();

  useEffect(() => {
    if (onVisibleRowsChange) {
      const visibleRows = table.getPaginationRowModel().rows;
      const visibleData = visibleRows.map((row) => row.original);
      onVisibleRowsChange(visibleData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting]);

  return (
    <Box sx={{ width: "100%", ...sx }} {...boxProps}>
      <Paper>
        <TableContainer>
          <MuiTable sx={{ minWidth: 650 }} aria-label="table">
            <TableHead table={table} />
            {loading ? (
              <TableSkeleton
                columnsCount={columns.length}
                rowsCount={pagination.pageSize}
              />
            ) : (
              <TableBody table={table} />
            )}
          </MuiTable>
        </TableContainer>
        {table.getRowCount() > 0 && (
          <TablePagination
            slotProps={{
              selectLabel: {
                sx: {
                  display: isMobile ? "none" : "block",
                },
              },
              select: {
                sx: {
                  display: isMobile ? "none" : "inline-flex",
                },
              },
            }}
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={table.getFilteredRowModel().rows.length}
            rowsPerPage={pagination.pageSize}
            page={pagination.pageIndex}
            labelRowsPerPage={t("table.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t("table.pageOf")} ${count}`
            }
            onPageChange={(_, page) => {
              table.setPageIndex(page);
            }}
            onRowsPerPageChange={(e) => {
              const size = e.target.value ? Number(e.target.value) : 10;
              table.setPageSize(size);
            }}
          />
        )}
      </Paper>
    </Box>
  );
};
